import React, { FC, RefObject, useEffect, useMemo, useRef } from 'react'
import { FormikHelpers } from 'formik'
import { ImageType } from 'react-images-uploading'

import ExampleAvatar from '../../../../assets/img/example/story.svg'
import Motions from '../../../../assets/img/socials/additional-icons/facebook-motions.svg'
import Gallery from '../../../../assets/img/socials/additional-icons/facebook-galery.svg'
import Smile from '../../../../assets/img/socials/additional-icons/facebook-smile.svg'
import Gif from '../../../../assets/img/socials/additional-icons/facebook-gif.svg'
import Comments from '../../../UI/Icons/Comments'
import Like from '../../../UI/Icons/Like'
import Share from '../../../UI/Icons/Share'
import Ellipsis from '../../../UI/Icons/Ellipsis'
import UploadImage from '../../../UploadPostImages'
import FirstCommentField from '../UI/CommentField'
import UploadVideo from '../../../UploadVideo'
import { TMediaCallbacks } from '../../../../pages/CreatePost'
import MessageField from '../UI/MessageField'
import VideoTagMultiselect from '../UI/VideoTagMultiselect'
import MultiLinkCarousel from '../UI/MultiLinkCarousel'
import LinkPreview from '../UI/LinkPreview'

import { TSocialDetails } from '../../../../selectors/ProjectSelector'
import {
  VIEW_TYPE,
  TPostSocialManagePost,
  TFieldChangePayload,
  TRemoveAttachmentPayload,
  TChangeAttachmentExtrafieldPayload,
  TAttachmentIds
} from '../../../../store/managePost/newTypes'
import { POST_TYPE, SOCIAL_NETWORK } from '../../../../models/post'

import './FacebookEditor.scss'
import VideoTitleInput from '../UI/VideoTitleInput'
import VideoThumbnail from '../UI/VideoThumbnail'
import { useDispatch } from '../../../../store'
import { getFacebookMentionsThunk , hashtagSearchThunk} from '../../../../store/managePost/thunks'
import { setPostSocialExtrafieldsAction } from '../../../../store/managePost/slice'

interface FacebookPreviewProps {
  post: TPostSocialManagePost
  onTextChange: (data: TFieldChangePayload) => void
  refMediaCallbaks: RefObject<TMediaCallbacks>
  addMediaToTemporaryLibrary: (data: ImageType) => void
  addThumbnailToAttachment: (data: ImageType, ids: TAttachmentIds) => void
  deleteMediaFromPostSocial: (id: TRemoveAttachmentPayload) => void
  setExtraField: (data: TChangeAttachmentExtrafieldPayload) => void
  addLinkToAttachments: (isChildLink: boolean) => void
  setCarouselActiveAttachmentIds: (ids: TAttachmentIds) => any
  deattachAttachment: (data: TRemoveAttachmentPayload) => void
  linkCarouselSelectedAttachmentIds: TAttachmentIds
  view: VIEW_TYPE
  restrictions: any
  isStory: boolean
  socialDetails: TSocialDetails
  publishDate: string
}

const FacebookEditor: FC<FacebookPreviewProps> = ({
  post,
  onTextChange,
  refMediaCallbaks,
  addMediaToTemporaryLibrary,
  deleteMediaFromPostSocial,
  setExtraField,
  deattachAttachment,
  addThumbnailToAttachment,
  socialDetails,
  setCarouselActiveAttachmentIds,
  linkCarouselSelectedAttachmentIds,
  isStory,
  addLinkToAttachments,
  publishDate,
  restrictions
}) => {
  const dispatch = useDispatch()
  const socialData = useMemo(() => socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL], [])

  useEffect(() => {
    if (post.type === POST_TYPE.POST_TYPE_LINK && !post.attachments.length) {
      addLinkToAttachments(false)
    }
  }, [post.attachments])

  const manageMediaProps = {
    attachments: post.attachments,
    isEditable: post.isEditable,
    addMediaToPost: addMediaToTemporaryLibrary,
    refMediaCallbaks,
    restrictions,
    removeMediaFromStore: deleteMediaFromPostSocial,
    social: SOCIAL_NETWORK.FACEBOOK_SOCIAL
  }

  const getFacebookMentions = async (query: string) =>
    await dispatch(getFacebookMentionsThunk(query))

  const getHashtags = async (query: string)=> 
    await dispatch(hashtagSearchThunk({social: SOCIAL_NETWORK.FACEBOOK_SOCIAL,query}))

  const setMentionToStore = (mention: any) => {
    dispatch(setPostSocialExtrafieldsAction({ social: SOCIAL_NETWORK.FACEBOOK_SOCIAL, mention }))
  }

  const attachmentNotToRemove = useMemo(() => {
    return post.attachments.find((attachment) => !attachment.toRemove) || null
  }, [post.attachments])

  return (
    <div className={`facebook-editor post-editor ${isStory && 'story-editor'}`}>
      <div className='facebook-content'>
        <div className='facebook-content__customer'>
          <div className='facebook-content__customer__avatar'>
            {socialData.logo.original ? (
              <img
                src={socialData.logo.original}
                alt='any'
                className='facebook-content__customer__avatar__img'
              />
            ) : (
              socialData.logo.default
            )}
          </div>
          <div className='facebook-content__customer__data'>
            <div className='facebook-content__customer__data__name'>{socialData.name}</div>
            {!isStory && (
              <div className='facebook-content__customer__data__last-seen'>{publishDate} &#xB7;</div>
            )}
          </div>
          <div className='facebook-content__customer__etc'>
            <Ellipsis />
          </div>
        </div>
        {/* // TODO restrictions messages for stories */}
        {restrictions.message && !isStory && (
          <div className='facebook-content__content'>
            <MessageField
              {...{
                className: 'facebook-content__content',
                value: post.message,
                onChange: onTextChange,
                isEditable: post.isEditable,
                setMention: setMentionToStore,
                setExtraField,
                getMentions: getFacebookMentions,
                getHashes: getHashtags,
                editorRaws: post.message_editor_state || null,
                attachmentIds: {
                  id: attachmentNotToRemove?.id || 0,
                  temporaryId: attachmentNotToRemove?.temporaryId
                },
                refMediaCallbaks,
                postType: post.type,
                social: post.social_network,
                link: attachmentNotToRemove?.extra_fields?.link || null,
                maxLength: 63206
              }}
            />
          </div>
        )}
        {restrictions.link && !restrictions.cards && (
          // need to create empty attachment entity and paste here if it not exist
          <LinkPreview
            {...{
              attachment: attachmentNotToRemove,
              isEditable: post.isEditable,
              deleteMediaFromPostSocial,
              setExtraField,
              refMediaCallbaks,
              addLinkToAttachments
            }}
          />
        )}
      </div>
      {restrictions.mediaRestrictions.image && (
        <div className='facebook-images'>
          <UploadImage {...manageMediaProps} />
        </div>
      )}
      {restrictions.mediaRestrictions.video && (
        <>
          <div className='facebook-videos'>
            <UploadVideo {...manageMediaProps} />
          </div>
          {!!post.attachments.length && (
            <div className='facebook-video-data'>
              <div className='facebook-video-data__thumbnail'>
                <VideoThumbnail
                  {...{
                    attachment: attachmentNotToRemove,
                    addThumbnailToAttachment: addThumbnailToAttachment,
                    isEditable: post.isEditable,
                    restrictions
                  }}
                />
              </div>
              <div className='facebook-video-data__text-fields'>
                <div className='facebook-video-data__text-fields__title'>
                  <VideoTitleInput
                    {...{
                      className: 'facebook-video-data__text-fields__title__input',
                      isEditable: post.isEditable,
                      attachmentIds: {
                        id: attachmentNotToRemove?.id || 0,
                        temporaryId: attachmentNotToRemove?.temporaryId
                      },
                      value: attachmentNotToRemove?.extra_fields?.title,
                      social: post.social_network,
                      setExtraField
                    }}
                  />
                </div>
                <div className='facebook-video-data__text-fields__tags'>
                  <VideoTagMultiselect
                    value={
                      attachmentNotToRemove?.extra_fields?.content_tags?.map((tag) => ({
                        id: String(tag.id),
                        name: String(tag.name)
                      })) || []
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {restrictions.cards && (
        <div className='facebook-link-carousel'>
          <MultiLinkCarousel
            {...{
              attachments: post.attachments,
              addMediaToTemporaryLibrary,
              deleteMediaFromPostSocial,
              isEditable: post.isEditable,
              deattachAttachment,
              logo: socialData.logo,
              setCarouselActiveAttachmentIds,
              linkCarouselSelectedAttachmentIds,
              setExtraField,
              restrictions,
              addLinkToAttachments,
              refMediaCallbaks
            }}
          />
        </div>
      )}
      {/* // TODO restrictions messages for stories */}
      {restrictions.message && !isStory && (
        <>
          <div className='facebook-assessments'>
            <div className='facebook-assessments__data-wrapper'>
              <div className='facebook-assessments__data-wrapper__motion'>
                <img
                  className='facebook-assessments__data-wrapper__motion__img'
                  src={Motions}
                  alt='any'
                />{' '}
                66
              </div>
              <div className='facebook-assessments__data-wrapper__shared'>Share: 1</div>
            </div>
          </div>
          <div className='facebook-actions'>
            <div className='facebook-actions__motion-wrapper'>
              <div className='facebook-actions__motion-wrapper__icon'>
                <Like /> Like
              </div>
              <div className='facebook-actions__motion-wrapper__icon'>
                <Comments /> Comment
              </div>
              <div className='facebook-actions__motion-wrapper__icon'>
                <Share /> Share
              </div>
              <div className='facebook-actions__motion-wrapper__avatar'>
                {socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.original ? (
                  <img
                    src={socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.original}
                    alt='any'
                    className='facebook-actions__motion-wrapper__avatar__img'
                  />
                ) : (
                  socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.default
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {!isStory ? (
        <div className='facebook-comment'>
          <div className='facebook-comment__avatar'>
            {socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.original ? (
              <img
                className='facebook-comment__avatar__img'
                src={socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.original}
                alt='any'
              />
            ) : (
              socialDetails[SOCIAL_NETWORK.FACEBOOK_SOCIAL].logo.default
            )}
          </div>
          <div className='facebook-comment__text-wrapper'>
            <FirstCommentField
              {...{
                className: 'facebook-comment__text-wrapper__input',
                isEditable: post.isEditable,
                value: post.first_comment,
                onChange: onTextChange,
                social: post.social_network,
                isExist: true
              }}
            >
              <div className='facebook-comment__text-wrapper__input__motions'>
                <img alt='any' src={Smile} />
                <img alt='any' src={Gif} />
                <img alt='any' src={Gallery} />
              </div>
            </FirstCommentField>

            <div className='facebook-comment__text-wrapper__hint'>
              Press Enter to send a comment.
            </div>
          </div>
        </div>
      ) : (
        <div className='facebook-story-reaction'>
          <FirstCommentField
            {...{
              className: 'facebook-story-reaction__text-wrapper',
              isEditable: post.isEditable,
              value: post.first_comment,
              onChange: onTextChange,
              social: post.social_network,
              isExist: true
            }}
          />
          <img className='facebook-story-reaction__motion' src={Motions} alt='any' />
        </div>
      )}
    </div>
  )
}

export default FacebookEditor
